import { z } from "zod";

/* ===========================================================================
 * US-E6.F6-0044 — Manual Reminder (@UC0041)
 * POST /v1/collections/:id/reminders — nhắc member chưa đóng (unpaid/overdue).
 * Enqueue outbox `reminder` + notification `in_app` (KHÔNG push thật — defer P8.5).
 * KHÔNG post ledger.
 * ========================================================================= */

/** Body: chọn 1 phần member để nhắc (vắng = tất cả due chưa đóng). */
export const sendRemindersRequestSchema = z
  .object({
    groupMemberIds: z.array(z.string().uuid()).max(500).optional(),
  })
  .strict();

export type SendRemindersRequest = z.infer<typeof sendRemindersRequestSchema>;

export const sendRemindersResponseSchema = z
  .object({
    remindedCount: z.number().int().nonnegative(),
  })
  .strict();

export type SendRemindersResponse = z.infer<
  typeof sendRemindersResponseSchema
>;

/* ===========================================================================
 * US-AIV2-0113 M04 — "Gửi nhắc qua Zalo" (@UC0041 @UC0239 · QĐ PO Q3 = CÓ, 28/09/2026)
 * GET /v1/collections/:id/reminders/share-targets — suất CHƯA ĐÓNG mà KHÔNG AI nhận được lời nhắc trong app
 * (cùng vị-từ `send()` lọc: `seatRecipientUserIds` rỗng). Thủ quỹ gửi TAY từng người qua Zalo (share sheet HĐH).
 *
 * 🔴 P5.3 — kênh NHẮC riêng tư tuyệt đối: MỖI DÒNG là MỘT câu cho MỘT người. Server chọn NỘI DUNG câu (`code` + tham
 * số: tên nhóm · tiêu đề/kỳ của đợt · tên ĐÚNG MỘT suất · số còn phải đóng · hạn — DỮ LIỆU THÔ); app dịch/thế chữ theo ngôn ngữ người gửi và
 * định dạng tiền bằng formatter chung — KHÔNG tính tiền, KHÔNG ghép tên người khác vào câu. App không gửi đi đâu: chủ
 * nhóm tự chọn người nhận trong Zalo. Link trong câu là link GẮN SUẤT có sẵn (`POST /groups/:id/members/:mid/status-link`,
 * `kind = member_status`) — KHÔNG phải link sổ công khai của nhóm.
 * ========================================================================= */

/** Mã câu soạn sẵn — client map `code → câu` theo locale (CLAUDE.md §7: backend trả code + params, không câu đã dịch). */
export const REMIND_SHARE_MESSAGE_CODE = "COLLECTION_REMIND_SHARE" as const;
/** Trần một trang. Tập nguồn đã bị cắt ở trần `REMIND_MAX` của `send()` (500) — cùng tập mà nút "Nhắc" chạm tới. */
export const REMIND_SHARE_TARGETS_MAX_LIMIT = 50;
/** Cursor opaque (base64url của `groupMemberId`) — luôn ngắn; chặn chuỗi khổng lồ trước khi giải mã. */
export const REMIND_SHARE_TARGETS_MAX_CURSOR_LENGTH = 128;

export const remindShareTargetsQuerySchema = z
  .object({
    cursor: z.string().max(REMIND_SHARE_TARGETS_MAX_CURSOR_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(REMIND_SHARE_TARGETS_MAX_LIMIT).optional(),
  })
  .strict();

export type RemindShareTargetsQuery = z.infer<typeof remindShareTargetsQuerySchema>;

/** Một suất cần nhắc tay. Tiền là chuỗi minor > 0 (BigInt trên wire — CLAUDE.md §3.1); `outstandingMinorOf` của server. */
export const remindShareTargetSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    /** Tên suất theo luật tên MỘT nguồn (PII-2). `null` = suất chưa đặt tên ⇒ app tự dịch nhãn mặc định (review #1071 LOW-4, §7 i18n). */
    memberName: z.string().min(1).nullable(),
    outstandingMinor: z.string().regex(/^[1-9][0-9]*$/),
    currency: z.string().regex(/^[A-Z]{3}$/),
    /**
     * Ghế lấy được link gắn suất không (cùng vị-từ cửa `POST /groups/:id/members/:mid/status-link` — QĐ điều phối 30/09 B). `false`
     * (ghế ngoài tập đang sống — `removed`/`join_pending`/`left`/`claimed`; từ M04b, QĐ PO 30/09 chiều, suất `unclaimed` ⇒ `true`)
     * ⇒ app KHÔNG gọi cửa link, gửi câu không link, không báo lỗi.
     */
    linkable: z.boolean(),
  })
  .strict();

export type RemindShareTarget = z.infer<typeof remindShareTargetSchema>;

export const remindShareTargetsResponseSchema = z
  .object({
    code: z.literal(REMIND_SHARE_MESSAGE_CODE),
    /**
     * Nhóm THẬT của đợt (review #1071 MED-2) — app tạo link gắn suất bằng chính id này, và báo lỗi rõ nếu lệch với nhóm của route
     * (không lặng lẽ gửi câu thiếu link).
     */
    groupId: z.string().uuid(),
    groupName: z.string().min(1),
    /**
     * DỮ LIỆU THÔ, không nhãn dựng sẵn (review #1071 LOW-4, §7 i18n): tiêu đề NGƯỜI DÙNG đặt (`null` nếu trống) + `periodKey` của đợt định kỳ;
     * app dịch "Tháng 9/2026" / "Đợt thu" qua ARB theo locale người gửi.
     */
    collectionTitle: z.string().min(1).nullable(),
    periodKey: z.string().min(1).nullable(),
    /** Hạn của ĐỢT (`collections.dueDate`, ngày lịch `YYYY-MM-DD`); `null` = đợt không đặt hạn ⇒ câu không nói hạn. */
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    data: z.array(remindShareTargetSchema),
    pageInfo: z.object({ hasNext: z.boolean(), nextCursor: z.string().nullable() }).strict(),
  })
  .strict();

export type RemindShareTargetsResponse = z.infer<typeof remindShareTargetsResponseSchema>;
